import { Fragment } from 'react'
import { Link } from '@tanstack/react-router'
import { useAuth } from '@/hooks/use-auth'
import { isFreeTier } from '@/lib/subscription-access'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarSeparator,
  useSidebar,
} from '@/components/ui/sidebar'
import {
  LayoutDashboard,
  Activity,
  Chrome,
  AlertTriangle,
  Bell,
  Globe,
  Wrench,
  Users,
  LogOut,
  Settings,
  ChevronsUpDown,
  ShieldCheck,
  HeartPulse,
  HandHeart,
  Bug,
  ExternalLink,
  ChevronLeft,
} from 'lucide-react'

type NavItem = {
  to: string
  label: string
  icon: typeof Activity
  /** Not on the free plan. A free organization sees "Pro" next to it and a notice on the page. */
  pro?: boolean
  /** Active only on this exact path, because every dashboard page sits under it. */
  exact?: boolean
  search?: Record<string, undefined>
}

// One group per job, split by a thin line.
export const navGroups: ReadonlyArray<ReadonlyArray<NavItem>> = [
  [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, exact: true },
    { to: '/dashboard/monitors', label: 'Uptime Monitors', icon: Activity },
    { to: '/dashboard/browser-checks', label: 'Browser Checks', icon: Chrome, pro: true },
  ],
  [
    { to: '/dashboard/incidents', label: 'Incidents', icon: AlertTriangle },
    { to: '/dashboard/alerts', label: 'Alerts', icon: Bell },
    { to: '/dashboard/maintenance', label: 'Maintenance', icon: Wrench, pro: true },
  ],
  [{ to: '/dashboard/status-pages', label: 'Status Pages', icon: Globe }],
  [
    { to: '/dashboard/team', label: 'Team', icon: Users },
    { to: '/dashboard/settings', label: 'Settings', icon: Settings, search: { billing: undefined, plan: undefined, tab: undefined } },
  ],
]

const ACTIVE_ROW = '[&.active]:bg-sidebar-accent [&.active]:font-medium [&.active]:text-primary [&.active_svg]:text-primary'

const adminNavItems = [
  { to: '/admin' as const, label: 'Admin · Users', icon: ShieldCheck },
  { to: '/admin/notifications' as const, label: 'Admin · Notifications', icon: HeartPulse },
  { to: '/admin/nonprofits' as const, label: 'Admin · Nonprofits', icon: HandHeart },
]

const adminExternalItems = [
  {
    href: 'https://api.uptrack.app/admin/errors',
    label: 'Admin · Errors',
    icon: Bug,
  },
]

/** A visible button on the edge of the bar. It is not on phones, where the bar is a sheet. */
function CollapseButton() {
  const { state, toggleSidebar } = useSidebar()
  const collapsed = state === 'collapsed'
  return (
    <button
      type="button"
      onClick={toggleSidebar}
      aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      aria-expanded={!collapsed}
      className="absolute top-1/2 -right-3 z-20 hidden size-6 -translate-y-1/2 items-center justify-center rounded-full border bg-background text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring md:flex"
    >
      <ChevronLeft aria-hidden="true" className={`size-4 transition-transform ${collapsed ? 'rotate-180' : ''}`} />
    </button>
  )
}

export function DashboardSidebar() {
  const { user, organization, logout } = useAuth()

  const plan = organization?.plan ?? 'free'
  const freeTier = isFreeTier(organization)
  const initials = user?.name
    ?.split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '?'

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link to="/dashboard">
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg border border-border bg-card">
                  <img src="/uptrack-mark.png" alt="" width={20} height={20} className="hidden size-5 dark:block" />
                  <img src="/uptrack-mark-light.png" alt="" width={20} height={20} className="size-5 dark:hidden" />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">Uptrack</span>
                  <span className="truncate text-xs text-muted-foreground">
                    {/* The free plan reads "Free plan". An org with no access at all (no paid plan, free plan closed) reads "No plan". */}
                    {freeTier ? 'Free plan' : plan === 'free' ? 'No plan' : `${plan.charAt(0).toUpperCase() + plan.slice(1)} Plan`}
                  </span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {navGroups.map((group, index) => (
          <Fragment key={group[0].to}>
            {index > 0 && <SidebarSeparator />}
            <SidebarGroup className="py-1">
              <SidebarGroupContent>
                <SidebarMenu>
                  {group.map((item) => (
                    <SidebarMenuItem key={item.to}>
                      <SidebarMenuButton asChild tooltip={item.label}>
                        <Link
                          to={item.to}
                          search={item.search}
                          activeOptions={{ exact: item.exact ?? false }}
                          className={ACTIVE_ROW}
                        >
                          <item.icon />
                          <span>{item.label}</span>
                          {freeTier && item.pro && <span className="ml-auto text-sm text-muted-foreground">Pro</span>}
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </Fragment>
        ))}

        {user?.is_admin && (
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {adminNavItems.map((item) => (
                  <SidebarMenuItem key={item.to}>
                    <SidebarMenuButton asChild tooltip={item.label}>
                      <Link
                        to={item.to}
                        className={ACTIVE_ROW}
                      >
                        <item.icon />
                        <span>{item.label}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
                {adminExternalItems.map((item) => (
                  <SidebarMenuItem key={item.href}>
                    <SidebarMenuButton asChild tooltip={item.label}>
                      <a href={item.href} target="_blank" rel="noopener noreferrer">
                        <item.icon />
                        <span>{item.label}</span>
                        <ExternalLink className="ml-auto h-3 w-3 opacity-60" />
                      </a>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          {/* User menu */}
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                >
                  <Avatar className="h-8 w-8 rounded-lg">
                    <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">{user?.name}</span>
                    <span className="truncate text-xs text-muted-foreground">{user?.email}</span>
                  </div>
                  <ChevronsUpDown className="ml-auto size-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                side="top"
                align="start"
                sideOffset={4}
              >
                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                  <Avatar className="h-8 w-8 rounded-lg">
                    <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">{user?.name}</span>
                    <span className="truncate text-xs text-muted-foreground">{user?.email}</span>
                  </div>
                </div>
                <DropdownMenuSeparator />
                {plan === 'free' && (
                  <DropdownMenuItem asChild>
                    <Link to="/pricing">
                      <Badge variant="secondary" className="mr-2 text-xs">Upgrade</Badge>
                      View Plans
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="cursor-pointer"
                  onSelect={async () => {
                    await logout()
                    window.location.href = '/'
                  }}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <SidebarRail />
      <CollapseButton />
    </Sidebar>
  )
}
